/**
 * Global active-trip store backed by localStorage + a tiny pub/sub.
 *
 * Purpose: keep the persistent Android FGS notification alive INDEPENDENTLY of
 * network state and React-tree unmounts. The notification reads from this store
 * (via subscription) instead of from network-driven props.
 *
 * Lifecycle:
 *  - Set when a trip enters `in_progress`.
 *  - Updated continuously by `useTripGpsTracker` with live km/min/speed/accuracy.
 *  - Cleared ONLY by an explicit user/server action (trip end / cancel).
 *
 * Survives:
 *  - Internet/Wi-Fi loss (no network reads/writes here).
 *  - Page reloads / app restarts.
 *  - React subtree unmounts (e.g. NoInternetOverlay replacing children).
 */
const STORAGE_KEY = 'driver_active_trip_v1';
const EVENT_NAME = 'driver:active-trip-changed';
const STARTING_EVENT_NAME = 'driver:active-trip-starting-changed';

// In-memory flag indicating a Start-Trip backend mutation is currently in
// flight. Used by DriverLayout to suppress the offline overlay during the
// brief window between tap and `in_progress` confirmation, so a momentary
// network flicker doesn't cover the screen with the offline UI.
let startingInFlight = false;

export interface ActiveTripState {
  bookingId: string;
  bookingNumber?: string | null;
  customerName?: string | null;
  startedAt?: string | null;
  // Live stats (updated by tracker)
  distanceKm: number;
  durationMin: number;
  speedKmh: number;
  gpsAccuracyM: number | null;
  hasGpsError: boolean;
}

// Memoize the parsed value so repeated `read()` calls return a referentially
// stable object reference when the underlying localStorage string hasn't
// changed. This makes `get()` safe to use directly as a `useSyncExternalStore`
// snapshot — without memoization, JSON.parse would produce a new object every
// call and trigger an infinite re-render loop in React 18.
let cachedRaw: string | null = null;
let cachedValue: ActiveTripState | null = null;

function read(): ActiveTripState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === cachedRaw) return cachedValue;
    cachedRaw = raw;
    cachedValue = raw ? (JSON.parse(raw) as ActiveTripState) : null;
    return cachedValue;
  } catch {
    cachedRaw = null;
    cachedValue = null;
    return null;
  }
}

function write(state: ActiveTripState | null) {
  try {
    if (state) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* quota / private mode */
  }
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch {
    /* SSR guard */
  }
}

export const activeTripStore = {
  get(): ActiveTripState | null {
    return read();
  },

  /** Start (or replace) the active trip. */
  start(args: {
    bookingId: string;
    bookingNumber?: string | null;
    customerName?: string | null;
    startedAt?: string | null;
  }) {
    const existing = read();
    // If same booking already active, preserve live stats; just refresh metadata
    const next: ActiveTripState = {
      bookingId: args.bookingId,
      bookingNumber: args.bookingNumber ?? existing?.bookingNumber ?? null,
      customerName: args.customerName ?? existing?.customerName ?? null,
      startedAt: args.startedAt ?? existing?.startedAt ?? null,
      distanceKm: existing?.bookingId === args.bookingId ? existing.distanceKm : 0,
      durationMin: existing?.bookingId === args.bookingId ? existing.durationMin : 0,
      speedKmh: existing?.bookingId === args.bookingId ? existing.speedKmh : 0,
      gpsAccuracyM:
        existing?.bookingId === args.bookingId ? existing.gpsAccuracyM : null,
      hasGpsError:
        existing?.bookingId === args.bookingId ? existing.hasGpsError : false,
    };
    write(next);
  },

  /** Update live stats for the current trip (no-op if no active trip). */
  updateStats(
    bookingId: string,
    stats: Partial<
      Pick<
        ActiveTripState,
        'distanceKm' | 'durationMin' | 'speedKmh' | 'gpsAccuracyM' | 'hasGpsError'
      >
    >,
  ) {
    const current = read();
    if (!current || current.bookingId !== bookingId) return;
    write({ ...current, ...stats });
  },

  /** Update metadata (booking number / customer name) for the current trip. */
  updateMeta(
    bookingId: string,
    meta: Partial<Pick<ActiveTripState, 'bookingNumber' | 'customerName' | 'startedAt'>>,
  ) {
    const current = read();
    if (!current || current.bookingId !== bookingId) return;
    write({ ...current, ...meta });
  },

  /** Clear the active trip (call on trip end / cancel). */
  clear() {
    write(null);
  },

  /**
   * Mark a Start-Trip mutation as in-flight. Call right before the backend
   * call that flips the booking to `in_progress`, and pair with
   * `clearStarting()` in a finally block.
   */
  markStarting() {
    if (startingInFlight) return;
    startingInFlight = true;
    try {
      window.dispatchEvent(new CustomEvent(STARTING_EVENT_NAME));
    } catch {
      /* SSR guard */
    }
  },

  clearStarting() {
    if (!startingInFlight) return;
    startingInFlight = false;
    try {
      window.dispatchEvent(new CustomEvent(STARTING_EVENT_NAME));
    } catch {
      /* SSR guard */
    }
  },

  isStarting(): boolean {
    return startingInFlight;
  },

  subscribeStarting(listener: () => void): () => void {
    const onCustom = () => listener();
    window.addEventListener(STARTING_EVENT_NAME, onCustom);
    return () => {
      window.removeEventListener(STARTING_EVENT_NAME, onCustom);
    };
  },

  /**
   * Subscribe to changes. Listener is called on every set/update/clear,
   * across tabs (via storage event) and within the same tab (via custom event).
   */
  subscribe(listener: () => void): () => void {
    const onCustom = () => listener();
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) listener();
    };
    window.addEventListener(EVENT_NAME, onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom);
      window.removeEventListener('storage', onStorage);
    };
  },
};
